// src/contexts/PersonalizedFeedContext.jsx

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';

const PersonalizedFeedContext = createContext(null);

export const usePersonalizedFeed = () => useContext(PersonalizedFeedContext);

export const PersonalizedFeedProvider = ({ children }) => {
  const { user } = useAuth();
  const [timeframe, setTimeframe] = useState('all');
  const queryClient = useQueryClient();

  const fetchPersonalizedFeed = useCallback(async () => {
    if (!user) return [];
    
    console.log(`PersonalizedFeedContext: Fetching feed for timeframe: ${timeframe}`);
    const { data, error } = await supabase.functions.invoke(
      `get-personalized-feed?timeframe=${timeframe}`
    );

    if (error) throw error;
    return data || [];
  }, [user, timeframe]);

  const {
    data: articles = [],
    isLoading: loading,
    error,
    refetch
  } = useQuery({
    queryKey: ['personalizedFeed', user?.id, timeframe],
    queryFn: fetchPersonalizedFeed,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // --- SIMPLIFIED: Removed manual useEffects for focus/visibility ---
  // React Query's global config handles this.

  // This effect is useful and specific, so we keep it. It forces a refresh
  // right after a user saves new preferences on the profile page.
  useEffect(() => {
    const preferencesJustUpdated = localStorage.getItem('preferencesJustUpdated');
    if (preferencesJustUpdated === 'true' && user) {
      console.log('PersonalizedFeedContext: Detected preference update, forcing refetch.');
      localStorage.removeItem('preferencesJustUpdated'); // Clear the flag
      refetch();
    }
  }, [user, refetch]);


  const value = useMemo(() => ({
    articles,
    loading,
    error: error ? error.message : null,
    timeframe,
    setTimeframe,
    refetch
  }), [articles, loading, error, timeframe, refetch]);

  return (
    <PersonalizedFeedContext.Provider value={value}>
      {children}
    </PersonalizedFeedContext.Provider>
  );
};